import supabase from "../services/supabase";
import { sendNotification } from './notificationService';

/**
 * Follow a user
 * @param {string} followedId - ID of the user to follow
 * @returns {Promise<Object>} - Result of the operation
 */
export const followUser = async (followedId) => {
  try {
    // First get the current user's ID
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error("User not authenticated");
    
    // Check if already following
    const { data: existingFollow } = await supabase
      .from("followers")
      .select("*")
      .eq("follower_id", user.id)
      .eq("followed_id", followedId)
      .single();
      
    if (existingFollow) {
      return { data: existingFollow, error: null, alreadyFollowing: true };
    }
    
    // Start a transaction by using .rpc() with a stored procedure
    // For simplicity, we'll do this in two operations, but in production
    // you should create a stored procedure for atomicity
    
    // Create follow relationship (triggers will automatically update counts)
    const { data, error } = await supabase
      .from("followers")
      .insert([
        { follower_id: user.id, followed_id: followedId }
      ])
      .select()
      .single();
    
    if (error) throw error;
    
    // 3. Notify the followed user
    try {
      await sendNotification({
        recipientId: followedId,
        actorId: user.id,
        type: 'follow'
      });
    } catch (_) {}

    return { data, error: null, alreadyFollowing: false };
  } catch (error) {
    console.error("Error following user:", error);
    return { data: null, error };
  }
};

/**
 * Unfollow a user
 * @param {string} followedId - ID of the user to unfollow
 * @returns {Promise<Object>} - Result of the operation
 */
export const unfollowUser = async (followedId) => {
  try {
    // First get the current user's ID
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) throw new Error("User not authenticated");
    
    // Delete the follow relationship (triggers will automatically update counts)
    const { data, error } = await supabase
      .from("followers")
      .delete()
      .eq("follower_id", user.id)
      .eq("followed_id", followedId);
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error("Error unfollowing user:", error);
    return { data: null, error };
  }
};

/**
 * Check if current user is following another user
 * @param {string} followedId - ID of the user to check
 * @returns {Promise<boolean>} - True if following, false otherwise
 */
export const isFollowingUser = async (followedId) => {
  try {
    // First get the current user's ID
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) return false; // Not authenticated, so not following anyone
    
    // Check if there's a follow relationship
    const { data, error } = await supabase
      .from("followers")
      .select("*")
      .eq("follower_id", user.id)
      .eq("followed_id", followedId)
      .single();
    
    if (error && error.code !== "PGRST116") throw error; // PGRST116 means no rows returned
    
    return !!data; // Convert to boolean - true if data exists, false otherwise
  } catch (error) {
    console.error("Error checking follow status:", error);
    return false;
  }
};

/**
 * Get user's followers
 * @param {string} userId - ID of the user to get followers for
 * @returns {Promise<Object>} - List of followers
 */
export const getUserFollowers = async (userId) => {
  try {
    // 1. Get follower IDs
    const { data: follows, error } = await supabase
      .from('followers')
      .select('follower_id')
      .eq('followed_id', userId);
    if (error) throw error;
    const ids = (follows || []).map(f => f.follower_id);
    if (ids.length === 0) return { data: [], error: null };
    // 2. Get user profiles
    const { data: profiles, error: profileError } = await supabase
      .from('user_profiles')
      .select('*')
      .in('user_id', ids);
    if (profileError) throw profileError;
    // Format for compatibility with UI
    const followers = profiles.map(profile => ({
      id: profile.user_id,
      email: profile.email,
      profile
    }));
    return { data: followers, error: null };
  } catch (error) {
    console.error('Error fetching followers:', error);
    return { data: [], error };
  }
};

/**
 * Get users followed by a user
 * @param {string} userId - ID of the user to get followed users for
 * @returns {Promise<Object>} - List of followed users
 */
export const getUserFollowing = async (userId) => {
  try {
    // 1. Get followed IDs
    const { data: follows, error } = await supabase
      .from('followers')
      .select('followed_id')
      .eq('follower_id', userId);
    if (error) throw error;
    const ids = (follows || []).map(f => f.followed_id);
    if (ids.length === 0) return { data: [], error: null };
    // 2. Get user profiles
    const { data: profiles, error: profileError } = await supabase
      .from('user_profiles')
      .select('*')
      .in('user_id', ids);
    if (profileError) throw profileError;
    // Format for compatibility with UI
    const following = profiles.map(profile => ({
      id: profile.user_id,
      email: profile.email,
      profile
    }));
    return { data: following, error: null };
  } catch (error) {
    console.error('Error fetching following:', error);
    return { data: [], error };
  }
};

/**
 * Get follower counts for a user
 * @param {string} userId - ID of the user
 * @returns {Promise<Object>} - Follower and following counts
 */
export const getUserFollowCounts = async (userId) => {
  try {
    // Get user profile with cached follower and following counts
    const { data: profile, error: profileError } = await supabase
      .from("user_profiles")
      .select("follower_count, following_count")
      .eq("user_id", userId)
      .single();
    
    if (profileError) throw profileError;
    
    return { 
      data: { 
        followers: profile.follower_count || 0, 
        following: profile.following_count || 0 
      },
      error: null
    };
  } catch (error) {
    console.error("Error fetching follow counts:", error);
    return { 
      data: { followers: 0, following: 0 },
      error
    };
  }
}; 
